import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import MasterItemPicker from '@/components/master/MasterItemPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  CommonRegistrationBlock, FormSection, finalizeNumber, registrationApi,
} from '@/components/registration';
import { mfgApi } from '@/pages/mfg/mfgApi';
import { itApi } from './itApi';

const STATUS_OPTS = [
  { value: 'in_stock', label: '在庫' },
  { value: 'in_use', label: '使用中' },
  { value: 'repair', label: '修理中' },
  { value: 'disposed', label: '廃棄' },
];

const NETWORKED = ['PC・ノートPC', 'サーバー', 'NAS・ストレージ', 'ネットワーク機器'];
const isNetworked = (cat) => NETWORKED.includes(cat);

const EMPTY = {
  name: '', asset_tag: '', category: '', maker: '', serial_no: '',
  status: 'in_stock', location: '', location_id: '', vendor: '',
  warranty_until: '', useful_life_years: '', salvage_value: '', description: '',
  ownership: 'owned', lease_end_date: '',
  // 調達情報（4台帳で共通の項目名）
  maker_pn: '', reference_url: '', supplier_id: '', supplier_name: '',
  // Consumables (asset_type='consumable') — populated only when relevant
  asset_type: 'durable',
  on_hand_qty: '', reorder_point: '', unit: '',
  purchase_unit: '', pack_qty: '', pack_price: '',
  department_id: '',
  // 部品受注入力リンク（bridge 経由の prefill / 既存資産の保持用）
  source_request_id: '', source_request_no: '', source_line_index: '',
  // 品目マスター連携
  item_id: '',
  // TECHS 発注に必要な情報（どの台帳でも同じ項目名・同じ並び）
  number_value: null, sku_group_id: '', order_category_id: '', order_category_name: '',
  uom: '', qty: '',
  // 共通ブロックの項目名（保存時に IT資産の項目名へ戻す）
  model_no: '', acquired_on: '', acquisition_price: '',
};

// 必須項目 — 技術資産（EngAssetFormDialog の REQ_LABEL）と同じ並びに揃える。
const REQ_LABEL = {
  name: '品名', asset_tag: '品番 / 資産番号',
  category: 'カテゴリ', model: '型式・仕様',
  location: '設置場所',
  on_hand_qty: '現在の在庫', unit: '単位',
  // A. 固定資産は会計上の4項目も新規登録時だけ必須（既存は「要入力」バッジのみ）
  purchase_date: '取得日', cost: '取得価格（円）', supplier: '仕入先（注文先）', source_request_no: 'REQ-No.',
};

/**
 * F6 · IT資産 / 消耗品を登録・編集。
 *
 * 2026-06 · 共通ブロック（`CommonRegistrationBlock`）に寄せた。かたまりは
 * 基本情報 → TECHS発注に必要な情報 → 保管・管理 → 在庫（消耗品）/ 購入・会計。
 * タブは廃止（足りない項目が別タブに隠れて気付けない問題を無くす）。
 * **保存を止める条件は変えていない**（下の submit を参照）。
 */
export default function AssetFormDialog({ open, onClose, asset, categories = [], defaultAssetType = 'durable', prefill = null, prefillIp = null, onSaved }) {
  const editing = !!asset;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // 品番ルールが1件も無いと番号を発番できない。その状態で資産番号を必須に
  // すると登録自体ができないので、ルールがあるときだけ必須にする。
  const [hasSkuRule, setHasSkuRule] = useState(true);
  // 品目マスター検索（部品受注入力・技術資産と同じ）
  const [itemQuery, setItemQuery] = useState('');
  const [departments, setDepartments] = useState([]);
  // VLAN/IP binding
  const [vlans, setVlans] = useState([]);
  const [vlanId, setVlanId] = useState('');
  const [availIps, setAvailIps] = useState([]);
  const [ipChoice, setIpChoice] = useState('__none__');
  const [boundIp, setBoundIp] = useState(null);
  const [prefillIpOpt, setPrefillIpOpt] = useState(null); // Omada検出IPのプリセット
  // A. 耐用年数の既定（カテゴリ別）— 未設定のときは「未設定」と出す（推測しない）
  const [fin, setFin] = useState(null);
  const lifeDefault = (fin?.useful_life_defaults || {})[form.category] ?? null;

  const isConsumable = form.asset_type === 'consumable';
  const createMark = editing ? null : <span className="text-rose-500">＊</span>;

  useEffect(() => {
    if (!open) return;
    itApi.skuTemplates().then((t) => setHasSkuRule((t || []).length > 0)).catch(() => setHasSkuRule(true));
    setItemQuery('');
    itApi.vlans().then(setVlans).catch(() => setVlans([]));
    itApi.financeSettings().then(setFin).catch(() => setFin(null));
    itApi.departments?.().then((d) => setDepartments(d || [])).catch(() => setDepartments([]));
    if (asset) {
      const merged = { ...EMPTY };
      Object.keys(EMPTY).forEach((k) => { merged[k] = asset[k] ?? EMPTY[k]; });
      merged.asset_type = asset.asset_type || 'durable';
      // 共通ブロックの項目名に写す
      merged.model_no = asset.model || '';
      merged.serial_no = asset.serial || '';
      merged.description = asset.notes || '';
      merged.acquired_on = (asset.purchase_date || '').slice(0, 10);
      merged.acquisition_price = asset.cost ?? '';
      merged.department_id = asset.assigned_to_department_id || '';
      merged.location = asset.location || '';
      merged.number_value = null;
      setForm(merged);
      if (asset.ip_entry_id) {
        setVlanId(asset.vlan_id || '');
        setIpChoice(asset.ip_entry_id);
        setBoundIp({ id: asset.ip_entry_id, address: asset.ip_address });
      } else {
        setVlanId(''); setIpChoice('__none__'); setBoundIp(null);
      }
      setPrefillIpOpt(null);
    } else {
      const initial = { ...EMPTY, asset_type: defaultAssetType, ...(prefill || {}) };
      setForm(initial);
      setBoundIp(null); setAvailIps([]);
      if (prefillIp?.ip_entry_id) {
        setVlanId(prefillIp.vlan_id || '');
        setIpChoice(prefillIp.ip_entry_id);
        setPrefillIpOpt({ id: prefillIp.ip_entry_id, address: prefillIp.ip_address });
      } else {
        setVlanId(''); setIpChoice('__none__'); setPrefillIpOpt(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asset, defaultAssetType]);

  // 既存の設置場所（文字列）を、ひとつの場所マスタの行に結び付ける。
  // 一致する行が無いときは「未登録の場所: …」として見せる（値は書き換えない）。
  useEffect(() => {
    if (!open || !form.location || form.location_id) return;
    registrationApi.locations().then((rows) => {
      const hit = (rows || []).find((r) => (r.name || '') === form.location);
      if (hit) setForm((p) => (p.location_id ? p : { ...p, location_id: hit.id }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.location]);

  useEffect(() => {
    if (!vlanId) { setAvailIps([]); return; }
    itApi.ips(vlanId, { status: 'available' }).then(setAvailIps).catch(() => setAvailIps([]));
  }, [vlanId]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));


  const pickItem = (it) => {
    setForm((p) => ({
      ...p,
      item_id: it.id,
      name: p.name || it.name || '',
      maker: p.maker || it.maker || '',
      model_no: p.model_no || it.model_no || it.maker_pn || '',
      acquisition_price: p.acquisition_price || (it.unit_price ?? it.cost ?? ''),
    }));
    setItemQuery('');
  };

  const onVlan = (v) => { setVlanId(v); setIpChoice(boundIp && asset?.vlan_id === v ? boundIp.id : '__none__'); };

  const sharedMode = form.number_value?.mode === 'shared';

  const submit = async () => {
    // 必須は**新規登録のときだけ**強制する（技術資産と同じ考え方）。
    const missing = [];
    if (!form.name.trim()) missing.push(REQ_LABEL.name);
    if (!editing) {
      const hasNumber = sharedMode || !!(form.number_value?.pending || form.number_value?.number)
        || !!(form.asset_tag || '').trim();
      if (hasSkuRule && !hasNumber) missing.push(REQ_LABEL.asset_tag);
      if (!(form.category || '').trim()) missing.push(REQ_LABEL.category);
      if (!(form.model_no || '').trim()) missing.push(REQ_LABEL.model);
      if (!(form.location_id || form.location || '').trim()) missing.push(REQ_LABEL.location);
      if (isConsumable) {
        const q = form.on_hand_qty;
        if (q === '' || q === null || q === undefined || Number.isNaN(Number(q)) || Number(q) < 0) {
          missing.push(REQ_LABEL.on_hand_qty);
        }
        if (!(form.unit || '').trim()) missing.push(REQ_LABEL.unit);
      } else {
        if (!(form.acquired_on || '').trim()) missing.push(REQ_LABEL.purchase_date);
        const c = form.acquisition_price;
        if (c === '' || c === null || c === undefined || Number.isNaN(Number(c)) || Number(c) < 0) {
          missing.push(REQ_LABEL.cost);
        }
        if (!(form.supplier_id || '').trim() && !(form.supplier_name || '').trim()
          && !(form.vendor || '').trim()) missing.push(REQ_LABEL.supplier);
        if (!(form.source_request_no || '').trim()) missing.push(REQ_LABEL.source_request_no);
      }
    }
    if (missing.length) {
      toast.error(`必須項目を入力してください: ${missing.map((m) => `「${m}」`).join('、')}`);
      return;
    }
    setSaving(true);
    let finalTag = form.asset_tag;
    if (!editing && !sharedMode) {
      try {
        finalTag = (await finalizeNumber(form.number_value)) || form.asset_tag;
      } catch (e) {
        toast.error(e?.response?.data?.detail || '資産番号の正式発番に失敗しました');
        setSaving(false);
        return;
      }
    }
    const num = (v) => (v === '' || v === null ? null : Number(v));
    const payload = {
      ...form,
      asset_tag: sharedMode ? null : finalTag,
      model: form.model_no || null,
      serial: form.serial_no || null,
      notes: form.description || null,
      purchase_date: form.acquired_on || null,
      assigned_to_department_id: form.department_id || null,
      location: form.location || null,
      sku_group_id: form.sku_group_id || form.number_value?.sku_group_code || null,
      order_category_id: form.order_category_id || null,
      order_category_name: form.order_category_name || null,
      name: form.name.trim(),
      cost: num(form.acquisition_price),
      useful_life_years: num(form.useful_life_years),
      salvage_value: num(form.salvage_value),
      on_hand_qty: num(form.on_hand_qty),
      reorder_point: num(form.reorder_point),
      pack_qty: num(form.pack_qty),
      pack_price: num(form.pack_price),
      source_line_index: form.source_line_index === '' ? null : Number(form.source_line_index),
      item_id: form.item_id || null,
      // 共通採番（EQP/CSM）— サーバーが保存時に確定する
      auto_shared_no: !editing && sharedMode,
    };
    delete payload.number_value;
    Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
    if (isConsumable) {
      payload.useful_life_years = null;
      payload.salvage_value = null;
      payload.serial = null;
      payload.warranty_until = null;
    } else {
      payload.purchase_unit = null;
      payload.pack_qty = null;
      payload.pack_price = null;
    }

    // IP binding intent (durable networked assets only)
    if (!isConsumable && isNetworked(form.category)) {
      if (ipChoice === '__auto__' && vlanId) { payload.vlan_id = vlanId; payload.ip_auto = true; }
      else if (ipChoice !== '__none__' && ipChoice && ipChoice !== boundIp?.id && vlanId) {
        payload.vlan_id = vlanId; payload.ip_entry_id = ipChoice;
      } else if (ipChoice === '__none__' && boundIp) {
        payload.ip_clear = true;
      }
    } else if (boundIp) {
      payload.ip_clear = true; // category no longer networked → release IP
    }

    try {
      if (editing) await itApi.updateAsset(asset.id, payload);
      else await itApi.createAsset(payload);
      toast.success(editing ? '資産を更新しました' : '資産を登録しました');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    'name', 'category', 'maker', 'notes',
    'number', 'sku_group', 'order_category', 'model_no', 'maker_pn',
    ...(isConsumable ? ['uom'] : []),
    'supplier', 'reference_url',
    'location', 'department', 'status',
    ...(isConsumable ? [] : ['serial_no', 'acquired_on', 'price']),
  ];
  const requiredKeys = [
    'name', 'number', 'category', 'model_no', 'location', 'order_category',
    ...(isConsumable ? ['uom'] : ['acquired_on', 'price', 'supplier']),
  ];
  // REQ-No. は IT資産だけの項目なので、要入力の案内にだけ足す（資産のときのみ）。
  const extraMissing = (!editing && !isConsumable && !(form.source_request_no || '').trim())
    ? [REQ_LABEL.source_request_no] : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined} data-testid="asset-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {editing ? (isConsumable ? '消耗品を編集' : '資産を編集') : (isConsumable ? '消耗品を登録' : '資産を登録')}
            <span data-testid="asset-form-type-badge"
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${isConsumable ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>
              {isConsumable ? '消耗品（数量管理）' : '資産（減価償却あり）'}
            </span>
          </DialogTitle>
        </DialogHeader>
        {!editing && (
          <p className="text-[11px] text-zinc-400 -mt-1" data-testid="asset-form-type-hint">
            種別（資産 / 消耗品）は発番する品番ルールの設定で自動的に決まります。
          </p>
        )}

        <div className="mt-1">
          {/* 標準ルール §7.1 #2 · 品目は共通の `MasterItemPicker` から選ぶ */}
          <MasterItemPicker
            testIdPrefix="asset-form-item"
            label="品目（マスターから検索 または 新規入力）"
            placeholder="品名・型番で品目マスターから検索…"
            helper="マスターから選ぶと品名・メーカー・型式が自動入力されます。"
            search={(q) => mfgApi.items({ q, all_departments: true, limit: 8 })}
            sources={['mfg']}
            onPick={pickItem}
            onQueryChange={setItemQuery}
            linked={form.item_id ? {
              label: form.name || '品目マスター連携済み',
              sku: form.asset_tag || '',
              sourceLabel: 'マスター連携済み',
            } : null}
            onUnlink={() => setForm((p) => ({ ...p, item_id: '' }))}
            unlinkLabel="解除"
          />
        </div>

        <CommonRegistrationBlock
          value={form} onChange={setForm} editing={editing}
          fields={fields} requiredKeys={requiredKeys} extraMissing={extraMissing}
          requiredTitle="要入力（登録前に入れてください）"
          askAcquisitionType
          register="it_asset" appCode="it_assets" currentNo={asset?.asset_tag || '—'}
          categories={categories} departments={departments} statusOptions={STATUS_OPTS}
          testPrefix="it-asset"
          onNumberIssued={(_sku, meta) => {
            // 発番した品番ルールの種別（資産 / 消耗品）をフォームに反映する
            if (!editing && meta?.asset_kind) setForm((p) => ({ ...p, asset_type: meta.asset_kind }));
          }}
          basicExtra={!hasSkuRule ? (
            <p className="text-[11px] text-amber-700" data-testid="asset-no-sku-rule-note">
              IT資産の品番ルールが未登録のため、番号なしでも登録できます（設定 → マスター → 品番と採番 で追加してください）。
            </p>
          ) : null}
          keepingExtra={(
            <>
              {!isConsumable && isNetworked(form.category) && (
                <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 space-y-3" data-testid="asset-ip-section">
                  <p className="text-xs font-semibold text-teal-700">ネットワーク（IPアドレス紐付け）</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">VLAN</Label>
                      <Select value={vlanId} onValueChange={onVlan}>
                        <SelectTrigger data-testid="asset-ip-vlan" className="h-10 mt-1"><SelectValue placeholder="VLANを選択" /></SelectTrigger>
                        <SelectContent>
                          {vlans.map((v) => <SelectItem key={v.id} value={v.id}>{v.name || v.key}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">IPアドレス</Label>
                      <Select value={ipChoice} onValueChange={setIpChoice} disabled={!vlanId}>
                        <SelectTrigger data-testid="asset-ip-choice" className="h-10 mt-1"><SelectValue placeholder="IPを選択" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">（紐付けなし）</SelectItem>
                          <SelectItem value="__auto__">次の空きIPを自動割当</SelectItem>
                          {boundIp && <SelectItem value={boundIp.id}>現在: {boundIp.address}</SelectItem>}
                          {prefillIpOpt && !boundIp && <SelectItem value={prefillIpOpt.id} data-testid="asset-ip-prefill-option">検出IP: {prefillIpOpt.address}</SelectItem>}
                          {availIps.map((e) => <SelectItem key={e.id} value={e.id}>{e.ip_address}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-400">紐付けると VLAN・IP管理 側で「使用中」になり、使用者・デバイス名が同期されます。</p>
                </div>
              )}
            </>
          )}
          extra={(
            <>
              {isConsumable && (
                <FormSection title="在庫（消耗品）" testId="asset-form-section-stock">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">現在の在庫 {createMark}</Label>
                      <Input data-testid="asset-input-onhand" type="number" min="0" step="any"
                        value={form.on_hand_qty} onChange={set('on_hand_qty')}
                        placeholder="例: 20" className="h-10 mt-1 tabular-nums" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">発注点（低在庫アラート）</Label>
                      <Input data-testid="asset-input-reorder" type="number" min="0" step="any"
                        value={form.reorder_point} onChange={set('reorder_point')}
                        placeholder="例: 5" className="h-10 mt-1 tabular-nums" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">在庫の単位 {createMark}</Label>
                      <Input data-testid="asset-input-unit" value={form.unit} onChange={set('unit')}
                        placeholder="例: 本 / 個 / 巻" className="h-10 mt-1" />
                      <p className="mt-0.5 text-[10px] text-zinc-400">在庫・発注点は必ずこの基本単位で持ちます</p>
                    </div>
                  </div>
                  <div className="rounded-lg border border-purple-100 bg-purple-50/40 p-3" data-testid="asset-pack-section">
                    <p className="text-xs font-semibold text-purple-800">まとめ売り（任意）</p>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">購入単位</Label>
                        <Input data-testid="asset-input-purchase-unit" value={form.purchase_unit}
                          onChange={set('purchase_unit')} placeholder="例: セット / 箱" className="h-10 mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">入数（1購入単位あたり）</Label>
                        <Input data-testid="asset-input-pack-qty" type="number" min="0" step="any"
                          value={form.pack_qty} onChange={set('pack_qty')} placeholder="例: 30"
                          className="h-10 mt-1 tabular-nums" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">パック価格（円）</Label>
                        <Input data-testid="asset-input-pack-price" type="number" min="0"
                          value={form.pack_price} onChange={set('pack_price')} placeholder="例: 9000"
                          className="h-10 mt-1 tabular-nums" />
                      </div>
                    </div>
                    {Number(form.pack_qty) > 0 && (
                      <p className="mt-2 text-[11px] font-semibold text-purple-900" data-testid="asset-pack-preview">
                        1{form.purchase_unit || 'パック'} = {Number(form.pack_qty)}{form.unit || '個'}
                        {Number(form.pack_price) > 0 && `・単価 ¥${Math.round((Number(form.pack_price) / Number(form.pack_qty)) * 100) / 100}`}
                      </p>
                    )}
                  </div>
                </FormSection>
              )}

              <FormSection title="購入・会計" testId="asset-form-section-finance">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-zinc-600">所有区分</Label>
                    <Select value={form.ownership || 'owned'} onValueChange={(v) => setForm((f) => ({ ...f, ownership: v, lease_end_date: v === 'owned' ? '' : f.lease_end_date }))}>
                      <SelectTrigger className="h-10 mt-1" data-testid="asset-input-ownership"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owned">自社所有</SelectItem>
                        <SelectItem value="lease">リース</SelectItem>
                        <SelectItem value="rental">レンタル</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.ownership && form.ownership !== 'owned' && (
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">
                        {form.ownership === 'lease' ? 'リース' : 'レンタル'}満了日（任意）
                      </Label>
                      <DateField testId="asset-input-lease-end" value={(form.lease_end_date || '').slice(0, 10)}
                        onChange={set('lease_end_date')} className="mt-1" inputClassName="h-10" />
                    </div>
                  )}
                  {!isConsumable && (
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">REQ-No.（部品受注入力の依頼番号） {createMark}</Label>
                      <Input data-testid="asset-input-req-no" value={form.source_request_no}
                        onChange={set('source_request_no')} placeholder="例: REQ-2026-0123"
                        className="h-10 mt-1 font-mono text-sm" />
                      <p className="mt-0.5 text-[10px] text-zinc-400">発注・請求と突き合わせるための番号です。部品受注入力から登録した場合は自動で入ります。</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-semibold text-zinc-600">購入先（自由入力・旧データ用）</Label>
                    <Input data-testid="asset-input-vendor" value={form.vendor} onChange={set('vendor')} className="h-10 mt-1" />
                    <p className="mt-0.5 text-[10px] text-zinc-400">
                      正は「仕入先（注文先）」です
                      {form.supplier_name ? `（選択中: ${form.supplier_name}）` : ''}
                      。ここは仕入先マスターに無い店の名前が入っている既存データ用です。
                    </p>
                  </div>
                  {!isConsumable && (
                    <>
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">保証期限</Label>
                        <DateField testId="asset-input-warranty" value={(form.warranty_until || '').slice(0, 10)}
                          onChange={set('warranty_until')} className="mt-1" inputClassName="h-10" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">耐用年数（年・減価償却用）</Label>
                        <Input data-testid="asset-input-life" type="number" min="0" step="0.5"
                          value={form.useful_life_years} onChange={set('useful_life_years')}
                          placeholder={lifeDefault != null ? `既定 ${lifeDefault} 年（カテゴリ）` : '未設定'} className="h-10 mt-1" />
                        <p className="mt-0.5 text-[10px] text-zinc-400" data-testid="asset-life-hint">
                          {lifeDefault != null
                            ? `空のままだとカテゴリの既定 ${lifeDefault} 年で計算します（設定 → IT資産マスタ → 会計）`
                            : '耐用年数 未設定 — カテゴリの既定も未設定のため、簿価は計算せず取得価額のまま表示します'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">残存価額（円）</Label>
                        <Input data-testid="asset-input-salvage" type="number" min="0"
                          value={form.salvage_value} onChange={set('salvage_value')} placeholder="0" className="h-10 mt-1" />
                      </div>
                    </>
                  )}
                </div>
                <p className="text-[11px] text-zinc-400">
                  ※ 取得価格・耐用年数・取得日を入力すると、現在簿価（定額法・月割り・残存1円）が自動計算されます。
                </p>
                {form.source_request_no && (
                  <p className="text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1.5" data-testid="asset-source-request">
                    発注元: 部品受注入力 {form.source_request_no}（受注データと連携済み）
                  </p>
                )}
              </FormSection>
            </>
          )}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="asset-form-cancel">キャンセル</Button>
          <Button data-testid="asset-form-submit" disabled={saving} onClick={submit}>
            {editing ? '更新する' : '登録する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
